/**
 * Root app component. Renders router, navigation, footer, and lazy-loaded pages.
 * @module App
 */
import React, {
  Suspense,
  useRef,
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
} from 'react';
import {
  clearLocalMap,
  getScrollBehavior,
  trackGoogleAnalyticsEvent,
} from '@Utils';
import { APP, ANALYTICS_COPY, type AccountTab } from '@CONSTANTS';
import { Navigation, Footer } from '@Components/page';
import { ErrorBoundary, Page } from '@Pages';
import { useRoute } from './router';
import {
  useDonationState,
  useNavigation,
  useDialogue,
  useAuth,
} from '@Contexts';
import {
  normalizeStateForComparison,
  SESSION_HISTORY_TRAVERSE_EVENT,
  type NavigationState,
} from './contexts/navigationHistory';
import './App.css';

type AppProps = {
  serverConstantsError?: Error | null;
};

/**
 * Main application component that handles core routing, state management, and UI rendering.
 *
 * @param {AppProps} props - Component props
 * @param {Error | null} [props.serverConstantsError] - Error from loading server constants, if any
 */
const App = ({ serverConstantsError }: AppProps) => {
  /* notifications, alerts, modals, overlays, etc */
  const { setShowAlert, setShowSideNav, setShowModal, showModal } =
    useDialogue();
  /**
   * State for account modal active tab
   */
  const [activeKey, setActiveKey] = useState<AccountTab>('Profile');

  const {
      navigateToSplash,
      navigateToFunnel,
      navContext,
      funnel,
      splash,
      step,
    } = useNavigation(),
    { authOut, isLoggedIn, isInitializing } = useAuth(),
    { resetDonationState, setCredentialsPath, credentialsPath } =
      useDonationState();

  // Track funnel state: 'reset' | 'progressing' | 'complete'
  const funnelStateRef = useRef<'reset' | 'progressing' | 'complete'>('reset');

  // Track previous funnel state to detect when leaving Confirmation
  const prevFunnelRef = useRef<string | undefined>(undefined);

  /** Prior navContext for detecting funnel → splash (e.g. Back from funnel with auth modal open). */
  const prevNavContextForAuthRef = useRef<'splash' | 'funnel' | undefined>(
    undefined
  );

  /** Serialized nav for closing credentials when navigation moves without popstate (e.g. replaceState). */
  const prevNavSignatureRef = useRef('');

  /**
   * Fires `campaign_path_seen` once per browser tab session for short-link entry
   * paths (`/r/`, `/bsky/`, `/x/`, `/li/`). sessionStorage dedupes so reloads on
   * the same path do not emit duplicate GA events.
   */
  useEffect(() => {
    const path = window.location.pathname;

    const isCampaignPath = ANALYTICS_COPY.CAMPAIGN_PATH_PREFIXES.some(
      (prefix) => path.startsWith(prefix)
    );

    if (!isCampaignPath) return;

    const storageKey = `${ANALYTICS_COPY.EVENTS.CAMPAIGN_PATH_SEEN}:${path}`;

    if (sessionStorage.getItem(storageKey)) return;

    sessionStorage.setItem(storageKey, 'true');

    trackGoogleAnalyticsEvent(ANALYTICS_COPY.EVENTS.CAMPAIGN_PATH_SEEN, {
      [ANALYTICS_COPY.PARAMS.CAMPAIGN_PATH]: path,
    });
  }, []);

  /**
   * Handles user logout process
   * Clears session data, resets application state, and shows logout alert
   */
  const handleLogOut = useCallback(() => {
    authOut();

    sessionStorage.removeItem('pb:session');
    setShowAlert((s) => ({
      ...s,
      login: false,
      logout: true,
      activate: false,
    })); // show alert
    setShowSideNav(false);
    resetDonationState(); // Use the new reset function
    funnelStateRef.current = 'reset'; // Reset funnel state
    navigateToSplash(''); // Navigate back to splash landing page
    clearLocalMap();
  }, [
    authOut,
    setShowAlert,
    setShowSideNav,
    navigateToSplash,
    resetDonationState,
  ]);

  /**
   * Handle Confirmation page redirect when navigating away from Confirmation
   * This ensures users go back to pol-donation and reset donation state
   */
  useLayoutEffect(() => {
    // Check if we're navigating away from Confirmation screen
    if (
      navContext === 'funnel' &&
      prevFunnelRef.current === 'confirmation' &&
      funnel !== 'confirmation'
    ) {
      // Redirect to pol-donation (intersection point)
      navigateToFunnel('pol-donation', 0);

      // Reset donation state when leaving Support
      resetDonationState();
    }

    // Update the previous funnel state for next comparison
    prevFunnelRef.current = funnel;
  }, [navContext, funnel, navigateToFunnel, resetDonationState]);

  // Close mobile sidenav when transitioning from funnel to splash
  useLayoutEffect(() => {
    if (navContext === 'splash') setShowSideNav(false);
  }, [navContext, setShowSideNav]);

  // Credentials modal is tied to funnel celebrate flow; clear it when leaving funnel (Back, etc.).
  // Also clears credentialsPath so desktop User nav sync does not reopen the modal.
  useLayoutEffect(() => {
    const prev = prevNavContextForAuthRef.current;
    prevNavContextForAuthRef.current = navContext;
    if (prev === 'funnel' && navContext === 'splash') {
      setCredentialsPath('');
      setShowModal((s) => ({ ...s, credentials: false }));
    }
  }, [navContext, setCredentialsPath, setShowModal]);

  // Session back/forward: popstate + in-app goBack (replaceState paths do not emit popstate).
  // Event is dispatched from navigationHistory / NavigationContext only; no history mutation here.
  useEffect(() => {
    const closeCredentialsOnSessionHistoryTraverse = () => {
      setCredentialsPath('');
      setShowModal((s) => (s.credentials ? { ...s, credentials: false } : s));
    };
    window.addEventListener(
      SESSION_HISTORY_TRAVERSE_EVENT,
      closeCredentialsOnSessionHistoryTraverse
    );
    return () =>
      window.removeEventListener(
        SESSION_HISTORY_TRAVERSE_EVENT,
        closeCredentialsOnSessionHistoryTraverse
      );
  }, [setCredentialsPath, setShowModal]);

  useLayoutEffect(() => {
    const navSig = normalizeStateForComparison({
      navContext,
      splash,
      funnel,
    } as NavigationState);
    const key = JSON.stringify({ ...navSig, step: step ?? -1 });
    const prev = prevNavSignatureRef.current;
    if (prev && prev !== key && (showModal.credentials || credentialsPath)) {
      setCredentialsPath('');
      setShowModal((s) => (s.credentials ? { ...s, credentials: false } : s));
    }
    prevNavSignatureRef.current = key;
  }, [
    navContext,
    funnel,
    splash,
    step,
    showModal.credentials,
    credentialsPath,
    setCredentialsPath,
    setShowModal,
  ]);

  // mobile form path control
  const route = useRoute();

  const handleSkipToMain = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      e.preventDefault();
      const main = document.getElementById('main-content');
      main?.focus();
      main?.scrollIntoView({ behavior: getScrollBehavior() });
    },
    []
  );

  return (
    <div className={'App flex-column'}>
      {/* Skip to main content -- accessibility feature; no URL hash */}
      <a
        href={'#main-content'}
        className={'skip-link'}
        onClick={handleSkipToMain}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSkipToMain(e);
          }
        }}
      >
        Skip to main content
      </a>
      {/* Navigation component */}
      <Navigation
        route={route}
        activeKey={activeKey}
        isLoggedIn={isLoggedIn}
        handleLogOut={handleLogOut}
        isInitializing={isInitializing}
        serverConstantsError={serverConstantsError}
      />
      <ErrorBoundary error={serverConstantsError || undefined}>
        <Suspense
          children={
            <Page
              isInitializing={isInitializing}
              handleLogOut={handleLogOut}
              setActiveKey={setActiveKey}
              isLoggedIn={isLoggedIn}
              activeKey={activeKey}
              splash={splash}
              route={route}
            />
          }
          fallback={
            <span
              aria-label={APP.BG_ARIA_LABEL}
              className={'background-image'}
              role={'img'}
            />
          }
        />
      </ErrorBoundary>
      <Footer />
    </div>
  );
};

export default React.memo(App);

// Le pouvoir arrête le pouvoir.
